/**
 * Optimistic topbar identity cache.
 *
 * A render-only localStorage snapshot used to paint the authenticated topbar
 * (avatar + name) instantly on boot, before /auth-session reconciles. Identity
 * only — no balance/ore/free-bets. NEVER holds a token. `avatarUrl` is the
 * SERVER-GATED value (null unless avatar_status === 'active'); the client never
 * re-derives visibility from `avatarStatus` (spec C1).
 *
 * Contract: SSR-safe (no-ops without `window`), never throws, returns null on
 * miss / parse error / shape mismatch. Mirrors the discipline of
 * handle401Logout.ts and the aurumrun_session_id accessors.
 */
export const TOPBAR_CACHE_KEY = 'aurumrun_topbar_cache'

const AVATAR_STATUSES = ['active', 'pending', 'rejected', 'removed_by_admin'] as const
export type CachedAvatarStatus = (typeof AVATAR_STATUSES)[number]

export interface TopbarCache {
  userId: string
  /** Account-level handle (users.username) — the fallback label. */
  username: string | null
  /**
   * Equipped character nickname (player_equipped.display_name). The PRIMARY
   * topbar label — paint shows `displayName ?? username`, mirroring
   * `useDisplayNickname`. null when the user never set a nickname.
   */
  displayName: string | null
  avatarUrl: string | null
  avatarStatus: CachedAvatarStatus | null
}

function isValid(v: unknown): v is TopbarCache {
  if (!v || typeof v !== 'object') return false
  const o = v as Record<string, unknown>
  if (typeof o.userId !== 'string' || o.userId.length === 0) return false
  if (o.username !== null && typeof o.username !== 'string') return false
  // `displayName` is a later addition — tolerate its absence (`undefined`) in
  // caches written by an older build, but reject a wrong-typed value.
  if (o.displayName !== null && o.displayName !== undefined && typeof o.displayName !== 'string') return false
  if (o.avatarUrl !== null && typeof o.avatarUrl !== 'string') return false
  if (o.avatarStatus !== null && !AVATAR_STATUSES.includes(o.avatarStatus as CachedAvatarStatus)) return false
  return true
}

export function readTopbarCache(): TopbarCache | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(TOPBAR_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!isValid(parsed)) {
      clearTopbarCache()
      return null
    }
    return {
      userId: parsed.userId,
      username: parsed.username,
      displayName: parsed.displayName ?? null,
      avatarUrl: parsed.avatarUrl,
      avatarStatus: parsed.avatarStatus,
    }
  } catch {
    return null
  }
}

export function writeTopbarCache(cache: TopbarCache): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(TOPBAR_CACHE_KEY, JSON.stringify({
      userId: cache.userId,
      username: cache.username ?? null,
      displayName: cache.displayName ?? null,
      avatarUrl: cache.avatarUrl ?? null,
      avatarStatus: cache.avatarStatus ?? null,
    }))
  } catch {
    // quota / private mode — non-fatal, just no optimistic paint next reload
  }
}

export function clearTopbarCache(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(TOPBAR_CACHE_KEY)
  } catch {
    // non-fatal
  }
}

/**
 * Convenience writer from a user-shaped object. Accepts the minimal identity
 * fields so callers don't import the full User type. `avatarUrl`/`avatarStatus`
 * MUST already be the server-gated values (spec C1) — pass exactly what the
 * store/`/auth-session` holds, do not re-derive.
 */
export function writeTopbarCacheFromUser(u: {
  id: string
  username?: string | null
  displayName?: string | null
  avatarUrl?: string | null
  avatarStatus?: CachedAvatarStatus | null
}): void {
  writeTopbarCache({
    userId: u.id,
    username: u.username ?? null,
    displayName: u.displayName ?? null,
    avatarUrl: u.avatarUrl ?? null,
    avatarStatus: u.avatarStatus ?? null,
  })
}
